import {StateCreator} from "zustand/vanilla";
import {
  EvidenceHydrationData,
  HydrateEvidenceRequest,
  HydrateEvidenceResponse,
  type EvidenceRef,
} from "@devplaninc/codeup";
import {companyEvidenceHydrationAPI} from "@/app/apiPaths";
import {jr} from "@/hooks/queries/response-handler";
import {
  emptyEvidenceHydrationRefs,
  documentEvidenceKey,
  evidenceHydrationKeys,
  evidenceRequestKey,
  hasEvidenceHydrationRefs,
  ticketKey,
} from "@/app/components/evidence/evidenceHydrationRefs";

export type EvidenceHydrationCache = EvidenceHydrationData & {
  loadingKeys: Record<string, true>
  notFoundKeys: Record<string, true>
}

export type EvidenceHydrationStoreState = {
  evidenceHydration: Record<number, EvidenceHydrationCache>
  hydrateEvidence(companyId: number, refs: HydrateEvidenceRequest): Promise<void>
}

type HydrationWaiter = {
  resolve: () => void
  reject: (error: unknown) => void
}

const pendingRefsByCompany = new Map<number, HydrateEvidenceRequest>();
const pendingWaitersByCompany = new Map<number, HydrationWaiter[]>();
const scheduledCompanies = new Set<number>();

export function emptyEvidenceHydrationData(): EvidenceHydrationCache {
  return {
    signals: [],
    changes: [],
    persons: [],
    discussionTopics: [],
    callNotes: [],
    features: [],
    tickets: [],
    documents: [],
    insights: [],
    loadingKeys: {},
    notFoundKeys: {},
  };
}

export const createEvidenceHydrationSlice: StateCreator<
  EvidenceHydrationStoreState,
  [],
  [],
  EvidenceHydrationStoreState
> = ((set, get) => ({
  evidenceHydration: {},
  hydrateEvidence: (companyId, refs) => {
    const current = get().evidenceHydration[companyId] ?? emptyEvidenceHydrationData();
    const missing = missingRefs(current, refs);
    if (!hasEvidenceHydrationRefs(missing)) return Promise.resolve();

    const loadingKeys = evidenceHydrationKeys(missing);
    set(s => ({
      ...s,
      evidenceHydration: {
        ...s.evidenceHydration,
        [companyId]: {
          ...(s.evidenceHydration[companyId] ?? emptyEvidenceHydrationData()),
          loadingKeys: {
            ...(s.evidenceHydration[companyId]?.loadingKeys ?? {}),
            ...Object.fromEntries(loadingKeys.map(key => [key, true] as const)),
          },
        },
      },
    }));

    const pending = pendingRefsByCompany.get(companyId) ?? emptyEvidenceHydrationRefs();
    pendingRefsByCompany.set(companyId, mergeRefs(pending, missing));

    const promise = new Promise<void>((resolve, reject) => {
      pendingWaitersByCompany.set(companyId, [
        ...(pendingWaitersByCompany.get(companyId) ?? []),
        {resolve, reject},
      ]);
    });

    if (!scheduledCompanies.has(companyId)) {
      scheduledCompanies.add(companyId);
      setTimeout(() => {
        scheduledCompanies.delete(companyId);
        const queuedRefs = pendingRefsByCompany.get(companyId) ?? emptyEvidenceHydrationRefs();
        const waiters = pendingWaitersByCompany.get(companyId) ?? [];
        pendingRefsByCompany.delete(companyId);
        pendingWaitersByCompany.delete(companyId);
        void fetchEvidenceHydration(companyId, queuedRefs, set)
          .then(() => waiters.forEach(waiter => waiter.resolve()))
          .catch(error => waiters.forEach(waiter => waiter.reject(error)));
      }, 0);
    }

    return promise;
  },
}));

async function fetchEvidenceHydration(
  companyId: number,
  refs: HydrateEvidenceRequest,
  set: Parameters<StateCreator<EvidenceHydrationStoreState>>[0],
): Promise<void> {
  const loadingKeys = evidenceHydrationKeys(refs);
  try {
    const js = await fetch(companyEvidenceHydrationAPI(companyId), {
      method: "POST",
      headers: {"content-type": "application/json"},
      body: JSON.stringify(HydrateEvidenceRequest.toJSON(refs)),
    }).then(jr);
    const response = HydrateEvidenceResponse.fromJSON(js);
    set(s => {
      const existing = s.evidenceHydration[companyId] ?? emptyEvidenceHydrationData();
      const data = response.data ?? EvidenceHydrationData.create();
      const insights = data.insights ?? [];
      const tickets = byId(data.tickets, ticketKey);
      const documents = byId(data.documents, document => document.ref ? documentEvidenceKey(document.ref) : "");
      const nextLoading = {...existing.loadingKeys};
      for (const key of loadingKeys) delete nextLoading[key];
      const returnedKeys = new Set<string>([
        ...data.signals.map(item => `signal:${item.id}`),
        ...data.changes.map(item => `change:${item.changeId}`),
        ...data.discussionTopics.map(item => `discussionTopic:${item.id}`),
        ...data.callNotes.flatMap(callNoteHydrationKeys),
        ...data.features.map(item => `feature:${item.id}`),
        ...Object.keys(tickets),
        ...Object.keys(documents),
        ...insights.map(item => `insight:${item.id}`),
      ]);
      const nextNotFound = {...existing.notFoundKeys};
      for (const key of loadingKeys) {
        if (!returnedKeys.has(key)) nextNotFound[key] = true;
      }
      return {
        ...s,
        evidenceHydration: {
          ...s.evidenceHydration,
          [companyId]: {
            signals: mergeBy(existing.signals, data.signals, item => item.id),
            changes: mergeBy(existing.changes, data.changes, item => item.changeId),
            persons: mergeBy(existing.persons, data.persons, item => item.personId),
            discussionTopics: mergeBy(existing.discussionTopics, data.discussionTopics, item => item.id),
            callNotes: mergeBy(existing.callNotes, data.callNotes, item => item.id),
            features: mergeBy(existing.features, data.features, item => item.id),
            tickets: mergeBy(existing.tickets, data.tickets, ticketKey),
            documents: mergeBy(existing.documents, data.documents, document => document.ref ? documentEvidenceKey(document.ref) : ""),
            insights: mergeBy(existing.insights, insights, item => item.id),
            loadingKeys: nextLoading,
            notFoundKeys: nextNotFound,
          },
        },
      };
    });
  } catch (error) {
    set(s => {
      const existing = s.evidenceHydration[companyId] ?? emptyEvidenceHydrationData();
      const nextLoading = {...existing.loadingKeys};
      for (const key of loadingKeys) delete nextLoading[key];
      return {
        ...s,
        evidenceHydration: {
          ...s.evidenceHydration,
          [companyId]: {...existing, loadingKeys: nextLoading},
        },
      };
    });
    throw error;
  }
}

function mergeRefs(a: HydrateEvidenceRequest, b: HydrateEvidenceRequest): HydrateEvidenceRequest {
  return {
    workspaceId: a.workspaceId || b.workspaceId,
    refs: uniqueRefsByRequestKey([...a.refs, ...b.refs]),
  };
}

function missingRefs(data: EvidenceHydrationCache, refs: HydrateEvidenceRequest): HydrateEvidenceRequest {
  return mergeRefs(emptyEvidenceHydrationRefs(), {
    workspaceId: refs.workspaceId,
    refs: refs.refs.filter(ref => {
      const key = evidenceHydrationKeys({...emptyEvidenceHydrationRefs(), refs: [ref]})[0];
      return key && isMissing(data, key, hydratedValue(data, key));
    }),
  });
}

function isMissing<T>(data: EvidenceHydrationCache, key: string, value: T | undefined): boolean {
  return !value && !data.loadingKeys[key] && !data.notFoundKeys[key];
}

function hydratedValue(data: EvidenceHydrationCache, key: string) {
  const [kind, id] = splitKey(key);
  switch (kind) {
    case "signal":
      return data.signals.find(item => item.id === id);
    case "change":
      return data.changes.find(item => item.changeId === id);
    case "discussionTopic":
      return data.discussionTopics.find(item => item.id === id);
    case "callNoteId":
      return data.callNotes.find(item => item.id === id);
    case "callNote": {
      const [providerType, providerNoteId] = splitScopedId(id);
      return data.callNotes.find(item => item.providerType === providerType && item.providerNoteId === providerNoteId);
    }
    case "feature":
      return data.features.find(item => item.id === id);
    case "issue":
      return data.tickets.find(item => ticketKey(item) === key);
    case "document":
      return data.documents.find(item => item.ref && documentEvidenceKey(item.ref) === key);
    case "insight":
      return data.insights.find(item => item.id === id);
    default:
      return undefined;
  }
}

function splitKey(key: string): [string, string] {
  const index = key.indexOf(":");
  if (index < 0) return [key, ""];
  return [key.slice(0, index), key.slice(index + 1)];
}

function splitScopedId(id: string): [string, string] {
  const index = id.indexOf(":");
  if (index < 0) return ["", ""];
  return [id.slice(0, index), id.slice(index + 1)];
}

function callNoteHydrationKeys(note: {id: string, providerType: string, providerNoteId: string}): string[] {
  const keys: string[] = [];
  if (note.id) keys.push(`callNoteId:${note.id}`);
  if (note.providerType && note.providerNoteId) keys.push(`callNote:${note.providerType}:${note.providerNoteId}`);
  return keys;
}

function byId<T>(
  values: T[],
  idOf: (item: T) => string,
): Record<string, T> {
  const out: Record<string, T> = {};
  for (const value of values) {
    const id = idOf(value);
    if (id) out[id] = value;
  }
  return out;
}

function mergeBy<T>(existing: T[], incoming: T[], idOf: (item: T) => string): T[] {
  const values = new Map<string, T>();
  for (const item of existing) {
    const id = idOf(item);
    if (id) values.set(id, item);
  }
  for (const item of incoming) {
    const id = idOf(item);
    if (id) values.set(id, item);
  }
  return Array.from(values.values());
}

function uniqueRefsByRequestKey(refs: EvidenceRef[]): EvidenceRef[] {
  const seen = new Set<string>();
  const out: EvidenceRef[] = [];
  for (const ref of refs) {
    const key = evidenceRequestKey(ref);
    if (!key || seen.has(key)) continue;
    seen.add(key);
    out.push(ref);
  }
  return out;
}
